import type React from 'react';
import { EvoMfeApp } from '@user-interfaces/evo-app-container';
import { useFeedbackCollectorParams } from '@/hooks/use-feedback-collector-url';
import type { FeatureFeedbackCollectorProps } from '@/types/feedbackCollector';

/**
 * A reusable feedback collector component that wraps the EvoMfeApp
 * micro-frontend host for the evo-feature-feedback-collector.
 *
 * This component automatically sources:
 * - userId from AuthProvider (authenticatedUser)
 * - customerName from CompanyContext (currentCompany)
 * - product, apiHost, and accountToken from configuration
 *
 * Only the `feature` prop needs to be specified per usage.
 *
 * Integration follows the Evo Application Integration guide:
 * https://compass.accessacloud.com/docs/Observability/pervasive-telemetry/feature-feedback/integration/evo-integration
 *
 * @example
 * ```tsx
 * <FeatureFeedbackCollector feature="Homepage" />
 * <FeatureFeedbackCollector feature="Cost Centres" />
 * ```
 */
export const FeatureFeedbackCollector: React.FC<FeatureFeedbackCollectorProps> = ({
  feature,
  className,
}) => {
  const params = useFeedbackCollectorParams(feature);

  if (!params) {
    return null;
  }

  return (
    <EvoMfeApp
      componentName="evo-feature-feedback-collector"
      componentVersion="1"
      componentParams={{
        ...params,
        reDisplayAfterDays: 0, // Always show for testing
      }}
      className={className}
    />
  );
};

export default FeatureFeedbackCollector;
